import { POST_HOG_API_HOST } from 'constants/routes';
import { PostHog, Properties, CapturedNetworkRequest, PostHogConfig } from 'posthog-js';
import { getItemsLocalStorage } from 'utils/helpers/storage';
import { EMPTY_STRING, LOCAL_STORAGE, REFERRAL } from 'constants/general';
import { POST_HOG_EVENT } from 'constants/events';
import { Auth0User } from 'interfaces/auth.interface';
import { isLegacySafari } from 'utils/browser.util';
import { isSupportPanelPage } from 'utils/support-panel.util';
import { SchoolFeature } from 'enums';
import {
  getLockedSchoolFeatures,
  subscribeToSchoolFeaturePolicy
} from 'utils/school-feature-policy.store';

const DEBUG_USER_ID = '9884b0af-dc9f-4207-964e-e4db537a2234';

let _postHogEvents: PostHog | undefined;
let _postHogErrors: PostHog | undefined;
let _isBootstrapped = false;
let _hasAliasedToUser = false;

type PostHogInstance = 'events' | 'errors';

const maskCapturedNetworkRequest = (
  data: CapturedNetworkRequest
): CapturedNetworkRequest => {
  if (!data.requestHeaders) {
    return data;
  }

  const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key'];
  const masked = { ...data.requestHeaders };
  sensitiveHeaders.forEach((header) => {
    const lowerHeader = header.toLowerCase();
    Object.keys(masked).forEach((key) => {
      if (key.toLowerCase() === lowerHeader) {
        masked[key] = '[masked]';
      }
    });
  });
  data.requestHeaders = masked;
  return data;
};

const getLegacySafariPostHogOptions = (): Partial<PostHogConfig> => ({
  capture_exceptions: false,
  capture_performance: false,
  disable_session_recording: true
});

const getModernSafariPostHogOptions = (): Partial<PostHogConfig> => ({
  capture_exceptions: true,
  capture_performance: true,
  session_recording: {
    maskAllInputs: true,
    recordCrossOriginIframes: true,
    recordHeaders: true,
    maskCapturedNetworkRequestFn: maskCapturedNetworkRequest
  }
});

const getPostHogBrowserOptions = () =>
  isLegacySafari()
    ? getLegacySafariPostHogOptions()
    : getModernSafariPostHogOptions();

const initPostHogInstance = async (
  type: PostHogInstance,
  apiKey: string | undefined
): Promise<PostHog | undefined> => {
  if (!apiKey) {
    console.warn(`PostHog ${type} API key not found`);
    return;
  }

  try {
    const postHog = (await import('posthog-js')).default;
    return postHog.init(apiKey, {
      api_host: POST_HOG_API_HOST,
      autocapture: false,
      secure_cookie: true,
      capture_pageview: true,
      capture_pageleave: true,
      persistence: 'memory',
      ...getPostHogBrowserOptions()
    });
  } catch (err) {
    console.warn(`PostHog ${type} initialization failed`, err);
    return;
  }
};

// Recording goes straight to PostHog, so the API can't refuse it like other locked features; the client must stop
// it. Kept stopped for the rest of the page session even if the lock is later lifted.
const stopSessionReplayIfSchoolLocked = () => {
  if (!getLockedSchoolFeatures().has(SchoolFeature.SESSION_REPLAY)) return;
  _postHogEvents?.stopSessionRecording();
  _postHogErrors?.stopSessionRecording();
};

subscribeToSchoolFeaturePolicy(stopSessionReplayIfSchoolLocked);

export const initPostHog = async () => {
  // The support panel shows customer details as plain text and session recording masks only
  // inputs, so analytics never starts on that page.
  if (isSupportPanelPage()) {
    return;
  }
  _postHogEvents = await initPostHogInstance(
    'events',
    process.env.REACT_APP_POST_HOG_EVENTS_API_KEY
  );

  _postHogErrors = await initPostHogInstance(
    'errors',
    process.env.REACT_APP_POST_HOG_ERRORS_API_KEY
  );
  _isBootstrapped = Boolean(_postHogEvents || _postHogErrors);
  stopSessionReplayIfSchoolLocked();
};

const resetPostHog = async () => {
  const resetInstance = (instance: PostHog | undefined, name: string) => {
    if (instance) {
      instance.reset(true);
    } else {
      console.warn(
        `PostHog ${name} is not initialized or reset is not available`
      );
    }
  };

  resetInstance(_postHogEvents, 'events');
  resetInstance(_postHogErrors, 'errors');
  _hasAliasedToUser = false;
  _isBootstrapped = false;
};

export const capturePostHogEvent = (
  eventName: string,
  properties?: Properties
) => {
  if (_postHogEvents) {
    _postHogEvents.capture(eventName, properties);
  } else {
    console.warn(
      'PostHog events is not initialized or capture is not available'
    );
  }
};

const isDebugUser = (): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { store } = require('store');
  const userId = store.getState().user.details?.id;
  return userId === DEBUG_USER_ID;
};

export const captureDebugPostHogEvent = (
  eventName: string,
  properties?: Properties
) => {
  if (isDebugUser()) {
    capturePostHogEvent(eventName, properties);
  }
};

export const captureException = (
  exception?: unknown,
  properties?: Properties
) => {
  if (_postHogErrors && exception) {
    _postHogErrors.captureException(exception, properties);
  } else {
    console.warn(
      'PostHog errors is not initialized or capture is not available'
    );
  }
};

export const identifyPostHogUser = (
  userId: string,
  properties?: Properties
) => {
  if (_postHogEvents) {
    _postHogEvents.identify(userId, properties);
  } else {
    console.warn(
      'PostHog events is not initialized or identify is not available'
    );
  }
};

export const saveReferralPostHogEvents = () => {
  const [
    referral,
    packToInstall,
    packId,
    focusTemplateToInstall,
    focusTemplateId
  ] = getItemsLocalStorage([
    LOCAL_STORAGE.SIGN_UP_REFERRAL,
    LOCAL_STORAGE.PACK_TO_INSTALL,
    LOCAL_STORAGE.PACK_ID,
    LOCAL_STORAGE.FOCUS_TEMPLATE_TO_INSTALL,
    LOCAL_STORAGE.FOCUS_TEMPLATE_ID
  ]);
  let postHogEvent = POST_HOG_EVENT.SIGN_UP_DIRECT;
  let postHogData = undefined;

  if (referral) {
    const [packName, focusTemplateName] = [
      packToInstall ? JSON.parse(packToInstall)?.pack_name : EMPTY_STRING,
      focusTemplateToInstall
        ? JSON.parse(focusTemplateToInstall)?.name
        : EMPTY_STRING
    ];

    if (referral === REFERRAL.HABIT_PACK) {
      postHogEvent = POST_HOG_EVENT.SIGN_UP_VIA_HABIT_PACK;
      postHogData = {
        packName,
        packId
      };
    } else {
      postHogEvent = POST_HOG_EVENT.SIGN_UP_VIA_FOCUS_TEMPLATE;
      postHogData = {
        focusTemplateName,
        focusTemplateId
      };
    }
  }
  _postHogEvents && capturePostHogEvent(postHogEvent, postHogData);
};

export const bootstrapPostHogAnonymous = async () => {
  if (_isBootstrapped) {
    return;
  }
  await initPostHog();
};

/**
 * Identifies the current PostHog session as an authenticated user.
 * Also aliases the anonymous id to the user id once (to stitch funnels).
 */
export const identifyPostHogAuthUser = async (
  auth0User: Auth0User,
  dbUserId: string
) => {
  if (!_isBootstrapped) {
    await bootstrapPostHogAnonymous();
  }
  const personProps: Properties = {
    name: auth0User.nickname || auth0User.name
  };

  if (_postHogEvents) {
    try {
      const currentDistinctId = (
        _postHogEvents as PostHog & { get_distinct_id?: () => string }
      )?.get_distinct_id?.();
      // Only alias once per app lifetime to avoid fragmentation
      if (
        currentDistinctId &&
        currentDistinctId !== dbUserId &&
        !_hasAliasedToUser
      ) {
        (
          _postHogEvents as PostHog & {
            alias?: (alias: string, distinctId: string) => void;
          }
        ).alias?.(dbUserId, currentDistinctId);
        _hasAliasedToUser = true;
      }
    } catch (e) {
      console.warn('PostHog alias failed', e);
    }

    _postHogEvents.identify(dbUserId, personProps);
  }

  auth0User?.['https://dashboard.focusbear.io/first_login'] &&
    saveReferralPostHogEvents();
};

export const resetPostHogToAnonymous = async () => {
  await resetPostHog();
  await bootstrapPostHogAnonymous();
};
